import { RelOfertasConversao, RelOfertasParticulares, RelOfertasPlano } from '../../../../interfaces/relatorios.dto';

export type Ordem = 'volume' | 'taxa' | 'az';
export type Escala = 'ampliada' | 'real';
export type Preset = 'hoje' | 'mes' | 'mes_anterior';

const MESES = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

// Teto de quadrados por operador: acima disso o waffle vira um bloco ilegível.
export const LIMITE_WAFFLE = 60;

// Abaixo disso a taxa do plano não diz muita coisa.
export const POUCOS_CASOS = 5;

export function iso(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// 1 casa, sem ",0": 4,8%, 50%, 66,7%. Sem base: "—".
export function formatarTaxa(parte: number, base: number): string {
  return base ? `${(Math.round((parte * 1000) / base) / 10).toLocaleString('pt-BR')}%` : '—';
}

// ALINE.D.S → AD; nome de uma parte só usa as duas primeiras letras.
export function iniciais(nome: string): string {
  const partes = (nome ?? '').split(/[.\s]+/).filter(Boolean);
  if (!partes.length) return '';
  if (partes.length === 1) return partes[0].slice(0, 2).toUpperCase();
  return (partes[0][0] + partes[1][0]).toUpperCase();
}

export interface Selo { texto: string; tipo: 'alerta' | 'bom' | 'neutro'; }

export function seloPlano(p: RelOfertasPlano, maisAceito: string | null, maisRecusado: string | null): Selo | null {
  if (!p.ofertado) return { texto: 'Sem oferta', tipo: 'neutro' };
  if (p.familia === maisRecusado) return { texto: 'Mais recusado', tipo: 'alerta' };
  if (p.familia === maisAceito) return { texto: 'Mais aceito', tipo: 'bom' };
  if (p.ofertado < POUCOS_CASOS) return { texto: 'Poucos casos', tipo: 'neutro' };
  return null;
}

export interface Waffle { quadros: boolean[]; resto: number; }

// Um quadrado por particular atendido, os que assinaram primeiro.
export function waffle(parte: number, base: number, limite = LIMITE_WAFFLE): Waffle {
  const total = Math.max(0, base);
  const n = Math.min(total, limite);
  const verdes = Math.min(Math.max(0, parte), n);
  return { quadros: Array.from({ length: n }, (_, i) => i < verdes), resto: total - n };
}

export function ordenar<T>(lista: T[], ordem: Ordem, parte: (x: T) => number, base: (x: T) => number, nome: (x: T) => string): T[] {
  const taxa = (x: T) => (base(x) ? parte(x) / base(x) : 0);
  const copia = [...lista];
  if (ordem === 'volume') copia.sort((a, b) => base(b) - base(a));
  if (ordem === 'taxa') copia.sort((a, b) => taxa(b) - taxa(a) || base(b) - base(a));
  if (ordem === 'az') copia.sort((a, b) => nome(a).localeCompare(nome(b), 'pt-BR'));
  return copia;
}

// Largura (0–100) de cada barra das etapas; a primeira é "Viram a oferta".
// Ampliada: etapas 2..n proporcionais à maior delas, e a primeira vira referência cheia.
// Real: tudo proporcional a quem viu a oferta.
export function largurasEtapas(valores: number[], escala: Escala): number[] {
  const base = valores[0] ?? 0;
  const maior = Math.max(0, ...valores.slice(1));
  return valores.map((v, i) => {
    if (i === 0) return escala === 'ampliada' || base ? 100 : 0;
    const ref = escala === 'ampliada' ? maior : base;
    return ref && v > 0 ? Math.max(1.5, Math.min(100, (v * 100) / ref)) : 0;
  });
}

export interface ResumoParticulares { particulares: number; atendimentos: number; taxa: string; destaque: { nome: string; parte: number } | null; }

// Sempre sobre a lista inteira, não só a página visível.
export function resumoParticulares(linhas: RelOfertasParticulares[]): ResumoParticulares {
  const particulares = linhas.reduce((s, l) => s + (l.particulares ?? 0), 0);
  const atendimentos = linhas.reduce((s, l) => s + (l.atendimentos ?? 0), 0);
  let destaque: ResumoParticulares['destaque'] = null;
  if (particulares && linhas.length > 1) {
    const maior = linhas.reduce((m, l) => (l.particulares > m.particulares ? l : m));
    destaque = { nome: maior.nome, parte: Math.round((maior.particulares * 100) / particulares) };
  }
  return { particulares, atendimentos, taxa: formatarTaxa(particulares, atendimentos), destaque };
}

// Comprimento da barra = volume de atendimentos em relação ao maior da lista.
export function larguraVolume(atendimentos: number, maior: number): number {
  return maior ? Math.min(100, (atendimentos * 100) / maior) : 0;
}

export function presetDoPeriodo(de: string, ate: string, hoje: string): Preset | null {
  const d = new Date(hoje + 'T00:00');
  if (de === hoje && ate === hoje) return 'hoje';
  if (de === iso(new Date(d.getFullYear(), d.getMonth(), 1)) && ate === hoje) return 'mes';
  if (de === iso(new Date(d.getFullYear(), d.getMonth() - 1, 1)) && ate === iso(new Date(d.getFullYear(), d.getMonth(), 0))) return 'mes_anterior';
  return null;
}

function ddmm(v: string): string {
  const [, m, d] = v.split('-');
  return `${d}/${m}`;
}

export function rotuloPeriodo(de: string, ate: string, hoje: string): string {
  const preset = presetDoPeriodo(de, ate, hoje);
  if (preset === 'hoje') return 'Hoje';
  if (preset) return MESES[Number((preset === 'mes' ? ate : de).split('-')[1]) - 1];
  return de === ate ? ddmm(de) : `${ddmm(de)} a ${ddmm(ate)}`;
}

export interface OperadorVisao { nome: string; iniciais: string; parte: number; base: number; taxa: string; waffle: Waffle; }

// Crédito do operador: compra no atendimento ou depois; a da pausa é de quem fechou.
export function montarOperadores(lista: RelOfertasConversao[], limite = LIMITE_WAFFLE): { venderam: OperadorVisao[]; semVenda: OperadorVisao[]; atendidosSemVenda: number } {
  const todos = lista.map(c => {
    const parte = (c.naFila ?? 0) + (c.depois ?? 0);
    const base = c.particulares ?? 0;
    return { nome: c.operador, iniciais: iniciais(c.operador), parte, base, taxa: formatarTaxa(parte, base), waffle: waffle(parte, base, limite) };
  });
  const venderam = ordenar(todos.filter(o => o.parte > 0), 'taxa', o => o.parte, o => o.base, o => o.nome);
  const semVenda = ordenar(todos.filter(o => o.parte <= 0), 'volume', o => o.parte, o => o.base, o => o.nome);
  return { venderam, semVenda, atendidosSemVenda: semVenda.reduce((s, o) => s + o.base, 0) };
}
